import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, CheckIcon } from 'lucide-react';
const STORAGE_KEY = 'madamyas_age_verified_at';
const VALID_FOR_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/3cysW5AXtjSc9R4hRWj3oj/001.png';
// Verification is intentionally NOT persisted — the disclaimer must appear on
// every visit to the site, regardless of how many times the user has been
// here before. This always returns false so the gate shows on every load.
export function readAgeVerification(): boolean {
  return false;
}
type AgeGateProps = {
  onVerified: () => void;
};
export function AgeGate({
  onVerified
}: AgeGateProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  // Lock body scroll while the gate is mounted
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  const handleEnter = () => {
    if (!confirmed) return;
    // No persistence — the disclaimer shows on every visit by design.
    setIsExiting(true);
    // Let the fade-out animation play, then notify parent to mount the site
    window.setTimeout(onVerified, 350);
  };
  const handleExit = () => {
    window.location.href = 'https://www.google.com';
  };
  return <AnimatePresence data-id="element-42">
      {!isExiting && <motion.div initial={false} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.35
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="age-gate-title" aria-describedby="age-gate-message" data-id="element-43">
          {/* Backdrop — fully opaque so nothing of the site shows through */}
          <div className="absolute inset-0 bg-ink" data-id="element-44" />

          {/* Decorative purple glows */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none" data-id="element-45" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-aubergine/30 rounded-full blur-3xl pointer-events-none" data-id="element-46" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 12,
        scale: 0.99
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.99
      }} transition={{
        duration: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative w-full max-w-3xl bg-onyx border border-gold/30 shadow-[0_0_80px_rgba(183,148,212,0.25)] max-h-[calc(100vh-2rem)] overflow-y-auto" data-id="element-47">
            {/* Decorative corner brackets */}
            <div className="absolute -top-px -left-px w-10 h-10 border-t-2 border-l-2 border-gold pointer-events-none" data-id="element-48" />
            <div className="absolute -top-px -right-px w-10 h-10 border-t-2 border-r-2 border-gold pointer-events-none" data-id="element-49" />
            <div className="absolute -bottom-px -left-px w-10 h-10 border-b-2 border-l-2 border-gold pointer-events-none" data-id="element-50" />
            <div className="absolute -bottom-px -right-px w-10 h-10 border-b-2 border-r-2 border-gold pointer-events-none" data-id="element-51" />

            <div className="px-6 sm:px-12 lg:px-16 py-8 sm:py-12 lg:py-14" data-id="element-52">
              {/* Logo + Shield */}
              <div className="flex flex-col items-center mb-6" data-id="element-53">
                <img src={LOGO_URL} alt="Madam Yas" className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-3" data-id="element-54" />
                <div className="flex items-center gap-2 text-gold" data-id="element-55">
                  <ShieldCheckIcon size={14} data-id="element-56" />
                  <p className="font-cinzel text-[10px] sm:text-xs tracking-[0.4em] uppercase" data-id="element-57">
                    Restricted Access
                  </p>
                </div>
              </div>

              {/* Headline */}
              <h2 id="age-gate-title" className="font-cormorant text-bone text-3xl sm:text-4xl font-light text-center mb-5 leading-tight" data-id="element-58">
                Age Verification & Disclaimer
              </h2>

              {/* Gold accent line */}
              <div className="flex justify-center mb-6" data-id="element-59">
                <div className="h-px w-16 bg-gold" data-id="element-60" />
              </div>

              {/* Message */}
              <div id="age-gate-message" className="text-silver-muted text-sm sm:text-[15px] leading-relaxed text-left sm:text-center mb-7 space-y-4" data-id="element-61">
                <p data-id="element-62">
                  This website contains mature themes and is intended
                  exclusively for adults who are at least eighteen (18) years of
                  age. By entering this website, you certify that you are 18
                  years of age or older and that viewing adult-oriented content
                  is legal in your jurisdiction.
                </p>
                <p data-id="element-63">
                  All content, images, videos, descriptions, communications,
                  conversations, roleplay scenarios, and other materials
                  presented on this website are provided solely for
                  entertainment, artistic expression, fantasy, educational, and
                  media production purposes. Any content appearing on this
                  website is intended as creative expression and should not be
                  interpreted as an offer, solicitation, or agreement for any
                  unlawful activity.
                </p>
                <p data-id="element-64">
                  By accessing this website, you acknowledge and agree that all
                  materials are viewed voluntarily for entertainment purposes
                  only and that you are solely responsible for compliance with
                  all applicable local laws and regulations.
                </p>
                <p className="text-silver" data-id="element-65">
                  If you are under the age of 18 or if adult-oriented content is
                  prohibited in your jurisdiction, you must exit this website
                  immediately.
                </p>
              </div>

              {/* Confirmation checkbox */}
              <label htmlFor="age-confirm" className="flex items-start gap-3 mb-7 cursor-pointer group select-none" data-id="element-66">
                <input id="age-confirm" type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="sr-only" data-id="element-67" />
                <span aria-hidden className={`relative flex-shrink-0 mt-0.5 w-5 h-5 border-2 flex items-center justify-center transition-all duration-200 ${confirmed ? 'border-gold bg-gold/15' : 'border-gold/40 bg-transparent group-hover:border-gold/70'}`} data-id="element-68">
                  {confirmed && <CheckIcon size={14} className="text-gold" data-id="element-69" />}
                </span>
                <span className="text-bone text-sm leading-snug" data-id="element-70">
                  I confirm I am at least{' '}
                  <span className="text-gold font-medium" data-id="element-71">18 years old</span>{' '}
                  and consent to viewing adult-oriented content.
                </span>
              </label>

              {/* Buttons */}
              <div className="flex flex-col gap-3" data-id="element-72">
                <button type="button" onClick={handleEnter} disabled={!confirmed} style={confirmed ? {
              backgroundColor: '#B794D4',
              color: '#07050A',
              borderColor: '#B794D4'
            } : undefined} className={`w-full px-6 py-4 text-sm uppercase tracking-[0.2em] font-semibold border-2 transition-all duration-300 flex items-center justify-center gap-3 ${confirmed ? 'shadow-[0_0_30px_rgba(183,148,212,0.35)] hover:shadow-[0_0_50px_rgba(183,148,212,0.55)] cursor-pointer' : 'border-gold/20 text-silver-muted/50 cursor-not-allowed'}`} data-id="element-73">
                  I Am 18+ — Enter Website
                  {confirmed && <span aria-hidden data-id="element-74">→</span>}
                </button>

                <button type="button" onClick={handleExit} className="w-full px-6 py-4 text-sm uppercase tracking-[0.2em] font-medium text-silver-muted border border-gold/20 hover:border-gold/40 hover:text-bone transition-all duration-300" data-id="element-75">
                  I Am Under 18 — Exit Website
                </button>
              </div>

              {/* Footer disclaimer */}
              <p className="text-silver-muted/70 text-[11px] sm:text-xs leading-relaxed text-center mt-7 pt-6 border-t border-gold/15" data-id="element-76">
                By entering this website, you acknowledge and agree to these
                terms and confirm that you meet the minimum age requirement.
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}